import * as React from 'react';
import classnames from 'classnames';
import { Trans } from 'react-i18next';

import { Banner } from '@bb-ui/react-library/dist/components/Banner';
import { BbThemeProvider } from '@bb-ui/react-library/dist/components/BbThemeProvider';
import { Link } from '@bb-ui/react-library';
import { Information } from '@bb-ui/icons/dist/small/Information';

import { useHelpLinks } from '../../hooks/useHelpLinks';
import { useIncrementalAuthDeprecation } from '../../hooks/useIncrementalAuthDeprecation';
import { useStyles } from './IncrementalAuthDeprecationBanner.styles';

/**
 * Where the banner is being rendered. This decides who sees it and which copy is used,
 * because the two placements know different things about the reader:
 *
 * - `homePage` renders only for users signed in with incremental auth, so it can address
 *   them directly ("your institution").
 * - `signIn` renders on the sign in screens, where nobody is signed in yet and the login
 *   method is therefore unknown, so it shows to everyone and stays impersonal.
 */
export type IncrementalAuthDeprecationPlacement = 'homePage' | 'signIn';

export interface IncrementalAuthDeprecationBannerProps {
    placement: IncrementalAuthDeprecationPlacement;
}

/**
 * Tells clients that signing in with an Illuminate account is being phased out, and points
 * them at the UAS migration documentation.
 *
 * One component serves both placements: they share the banner, icon, link and styling, and
 * differ only in who sees them and which string they use. The theme is pinned rather than
 * inherited so the banner is the same blue everywhere - the sign in dialog is dark themed
 * but nests a light themed provider around the region picker, which would otherwise give
 * the same banner two different colours.
 */
export const IncrementalAuthDeprecationBanner: React.FC<
    IncrementalAuthDeprecationBannerProps
> = ({ placement }) => {
    const classes = useStyles();
    const getHelpLink = useHelpLinks();
    const { isAnnounced, affectsCurrentUser } = useIncrementalAuthDeprecation();

    const isHomePage = placement === 'homePage';
    const isVisible = isHomePage ? affectsCurrentUser : isAnnounced;

    if (!isVisible) {
        return null;
    }

    const testId = isHomePage
        ? 'incremental-auth-deprecation-banner'
        : 'incremental-auth-deprecation-notice';

    const linkProps = {
        href: getHelpLink('uasMigration'),
        target: '_blank',
        rel: 'noopener noreferrer',
        'data-testid': `${testId}-link`,
    };

    return (
        <BbThemeProvider theme="light">
            <Banner
                severity="info"
                icon={<Information />}
                className={classnames(classes.banner, {
                    [classes.homePageBanner]: isHomePage,
                })}
                data-testid={testId}
            >
                {isHomePage ? (
                    <>
                        { /* prettier-ignore */ }
                        <Trans i18nKey="homePage.incrementalAuthDeprecation.text">
                            Signing in with an <span lang="en">Illuminate</span> account is going away.
                            Institutions must switch to signing in with an institutional account.
                            <Link {...linkProps}>[Learn how →]</Link>
                        </Trans>
                    </>
                ) : (
                    <>
                        { /* prettier-ignore */ }
                        <Trans i18nKey="auth.incrementalAuthDeprecation.notice">
                            Signing in with an <span lang="en">Illuminate</span> account is going away.
                            Institutions must switch to signing in with an institutional account.
                            <Link {...linkProps}>[Learn how →]</Link>
                        </Trans>
                    </>
                )}
            </Banner>
        </BbThemeProvider>
    );
};

export default IncrementalAuthDeprecationBanner;
